import { DxContainer, DxGrid, DxSpacer } from '@dvag/design-system-react';
import type { FC } from 'react';
import { useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { appNames } from '@dvag/dfs-constant-config/app-names';
import { sharedAppRoutes } from '@dvag/dfs-constant-config/shared-app-names';
import { syncOrchestratorUrl } from '@dvag/dfs-orchestrator-client/navigation';
import { navigate } from '@dvag/dfs-orchestrator-client/messengers';

import { UnsavedOrPendingModal } from 'component/UnsavedOrPendingModal';
import { route } from 'config/route';
import { personApi } from 'service/api/personApi';
import { buildPath } from 'service/buildPath';
import { handleNavigateToPath } from 'service/handleNavigateToPath';
import { useContinuation } from 'utils/useContinuation';

import { KundenstammdatenSchnellerfassungWebComponent } from './KundenstammdatenSchnellerfassungWebComponent';

export const ManageHousehold: FC = () => {
  const { householdId, returnUrl } = useParams<{ householdId: string; returnUrl: string }>();
  const [hhId, setHHId] = useState(() => householdId ?? '');

  const { continuation, getContinuationHandler, getCanContinueHandler } = useContinuation();

  const onCanContinueChange = useMemo(() => getCanContinueHandler(), [getCanContinueHandler]);

  const prevDirtyRef = useRef<boolean | null>(null);

  const updateLastAccessedHouseholdList = (id: string) => {
    if (id) {
      personApi().updateLastAccessedHouseholdList(id);
    }
  };

  const navigateBack = () => {
    const householdIdPath = householdId ?? hhId;

    if (householdIdPath && returnUrl) {
      const basePath = `/${appNames.webvpCatalog}/haushalt/${householdIdPath}`;
      const standalonePath = `${basePath}/foerderrechner`;
      const avdPath = `${basePath}/avd-beratungsmodul/foerderrechner`;
      let decodedReturnUrl = '';
      try {
        decodedReturnUrl = decodeURIComponent(returnUrl);
      } catch {
        navigate(standalonePath);
        return;
      }
      const returnPath = decodedReturnUrl.split('?')[0];
      const isCalculatorPath =
        returnPath === standalonePath ||
        returnPath === avdPath ||
        returnPath.startsWith(`${avdPath}/`);

      navigate(isCalculatorPath ? decodedReturnUrl : standalonePath);
      return;
    }

    const path = householdIdPath
      ? sharedAppRoutes.advisor.householdDashboard
      : sharedAppRoutes.advisor.householdSearch;

    handleNavigateToPath(path, householdIdPath, appNames.advisor);
  };

  const navigateToKss = (kundennummer: string) => {
    if (!hhId || !kundennummer) {
      return;
    }

    navigate(`/${appNames.person}/haushalt/${hhId}/analyse/persoenliche-daten/${kundennummer}`);
  };

  const navigateToCreatedHousehold = (createdHouseholdId: string) => {
    if (!createdHouseholdId) {
      return;
    }

    setHHId(createdHouseholdId);
    updateLastAccessedHouseholdList(createdHouseholdId);

    syncOrchestratorUrl(
      buildPath(route.manageHousehold, {
        householdId: createdHouseholdId,
      }),
    );
  };

  const onDirtyStateChange = (isDirty: boolean) => {
    onCanContinueChange(!isDirty);

    const prevDirty = prevDirtyRef.current;
    prevDirtyRef.current = isDirty;

    if (prevDirty === true && isDirty === false && householdId) {
      updateLastAccessedHouseholdList(householdId);
    }
  };

  return (
    <>
      <DxContainer color="transparent" data-testid="personal-data-form">
        <DxGrid mq1="12/*">
          <DxContainer type="page" color="transparent">
            <DxSpacer size="16v" />
            <KundenstammdatenSchnellerfassungWebComponent
              onNavigateBack={navigateBack}
              onNavigateToKss={navigateToKss}
              onDirtyStateChange={onDirtyStateChange}
              onHouseholdCreated={navigateToCreatedHousehold}
              getContinuationHandler={getContinuationHandler}
            />
            <DxSpacer size="16v" />
          </DxContainer>
        </DxGrid>
      </DxContainer>

      <UnsavedOrPendingModal
        isKssSpelling
        showPending={false}
        showUnsaved={continuation !== undefined}
        onCancel={continuation?.onCancel}
        onConfirm={continuation?.onConfirm}
      />
    </>
  );
};
